import {
    getSelectedShippingAddress,
    getShippingAddress,
} from '@Builder/QuoteDetailPage/QuoteDetailsSummaryCard/components/ShippingSection/logic'
import moment, { Moment } from 'moment-timezone'
import { createSelector } from 'reselect'
import { useSelector } from 'react-redux'

import { RecommendedQuantitiesToHitDeliveryDate_recommendedQuantitiesToHitDeliveryDate as RecommendedQuantitiesToHitDeliveryDateType } from '~/apps/consumer/pages/Quotes/Builder/QuoteDetailPage/FilesTab/CostAndLeadTimePage/components/PartQLLTView/__generated__/RecommendedQuantitiesToHitDeliveryDate'
import { getOrderLateBy } from '~/apps/consumer/pages/Quotes/Builder/QuoteDetailPage/FilesTab/CostAndLeadTimePage/logic'
import { PROVISIONED_UI_FLAGS } from '~/apps/consumer/pages/Quotes/Builder/QuoteDetailPage/logic'
import {
    getMachiningSubItemsPriceDisplay,
    getPriceDisplay,
} from '~/lib/getPriceDisplay'
import isFlagEnabled from '~/lib/isFlagEnabled'
import {
    DFM_FAILURE,
    DFM_WARNING,
    PROCESSES_WITH_THREADED_TAG,
    PROCESSID_IM,
} from '~/lib/partsConstants'
import {
    DRAFT,
    NEEDS_RFQ,
    QUANTITY_RANGE_UNAVAILABLE,
    REORDERING,
} from '~/lib/quoteStatuses'
import {
    getUserIdentity,
    isUserHasBusinessAccount,
} from '~/redux-modules/identity'
import { isCoreSuccessState } from '~/redux-modules/query-refactor/selector'
import { StateData } from '~/redux-modules/quote-qllt/types'
import {
    CostAndLeadTime_quote_quoteModels_items,
    CostAndLeadTime_quote_quoteModels_items_selectedQuoteModelRevision_dfmFeedback as FeedBackType,
} from '~/redux-modules/quote-qllt/__generated__/CostAndLeadTime'
import { FictivStateType } from '../fictivStateType'

/**
 * @deprecated Assume flag is always true
 */
export const isQueryDetailsQueryRefactorOn = (state: FictivStateType) =>
    isFlagEnabled(
        getUserIdentity(state),
        PROVISIONED_UI_FLAGS.QUOTE_DETAILS_QUERY_REFACTOR,
        'true',
    )

export const getNeedByDate = (state: FictivStateType) => {
    const isQueryDetailsQueryRefactorEnabled =
        isQueryDetailsQueryRefactorOn(state)

    if (isQueryDetailsQueryRefactorEnabled) {
        return state.queryRefactor?.coreQuoteDetails?.coreQuoteDetailsData
            ?.needByDate
    } else {
        return state.dfmLayers?.quote?.needByDate
    }
}

const getQuoteId = (state: FictivStateType) => {
    const isQueryDetailsQueryRefactorEnabled =
        isQueryDetailsQueryRefactorOn(state)

    if (isQueryDetailsQueryRefactorEnabled) {
        return state.queryRefactor?.coreQuoteDetails?.coreQuoteDetailsData?.id
    } else {
        return state.dfmLayers?.quote?.id
    }
}
const getQuote = (state: FictivStateType) => {
    const isQueryDetailsQueryRefactorEnabled =
        isQueryDetailsQueryRefactorOn(state)

    if (isQueryDetailsQueryRefactorEnabled) {
        return state.queryRefactor?.coreQuoteDetails?.coreQuoteDetailsData
    } else {
        return state.dfmLayers?.quote
    }
}

export const isNoTargetDeliveryDateState = createSelector(
    getNeedByDate,
    getQuoteId,
    (state: FictivStateType) => state?.quoteDetail,
    (needByDate, quoteId, quoteDetails) => {
        const estDate =
            quoteDetails?.[quoteId]?.selectedShippingOption?.deliveredByDate

        if (!estDate || !needByDate) {
            return true
        }
        return false
    },
)

export const isTargetDeliveryDateMetState = createSelector(
    getNeedByDate,
    getQuoteId,
    (state: FictivStateType) => state?.quoteDetail,
    (needByDate, quoteId, quoteDetails) => {
        const estDate =
            quoteDetails?.[quoteId]?.selectedShippingOption?.deliveredByDate

        if (estDate && needByDate) {
            const estDateNoTime = moment(moment.utc(estDate))
                .local()
                .format('YYYY-MM-DD')
            const needByDateNoTime = moment(moment.utc(needByDate))
                .local()
                .format('YYYY-MM-DD')

            const isOnTime = moment(estDateNoTime).isSameOrBefore(
                needByDateNoTime,
                'day',
            )

            return isOnTime
        }
        return false
    },
)

export const isTargetDeliveryDateNotMetState = createSelector(
    getNeedByDate,
    getQuoteId,
    (state: FictivStateType) => state?.quoteDetail,
    (needByDate, quoteId, quoteDetails) => {
        const estDate =
            quoteDetails?.[quoteId]?.selectedShippingOption?.deliveredByDate

        if (estDate && needByDate) {
            const estDateNoTime = moment(moment.utc(estDate))
                .local()
                .format('YYYY-MM-DD')
            const needByDateNoTime = moment(moment.utc(needByDate))
                .local()
                .format('YYYY-MM-DD')
            const isLate = moment(estDateNoTime).isAfter(
                needByDateNoTime,
                'day',
            )

            return isLate
        }
        return false
    },
)

export const isQLTOptimizationOn = (state: FictivStateType) => {
    return isFlagEnabled(
        getUserIdentity(state),
        PROVISIONED_UI_FLAGS.QLT_OPTIMIZATION,
        'true',
    )
}

export const showQLTFeature = createSelector(
    isQLTOptimizationOn,
    isUserHasBusinessAccount,
    getUserIdentity,
    (state: FictivStateType) => state?.quoteQllt?.businessAccountCLTFeature,
    (
        isQLTOptimizationFlagOn,
        userHasBussinessAccount,
        identity,
        businessAccountCLTFeature,
    ) => {
        const userHasCLTOptimizerFeature =
            businessAccountCLTFeature?.isEnabled || false
        const isCLTFeatureEnabled =
            isQLTOptimizationFlagOn && userHasCLTOptimizerFeature
        const leadTimeOptimizationAccess =
            isCLTFeatureEnabled && userHasBussinessAccount

        const isFictivEmpoloyee = identity?.isAdmin && isQLTOptimizationFlagOn

        const showLTOInterface = leadTimeOptimizationAccess || isFictivEmpoloyee

        return { showLTOInterface }
    },
)

export const isQLTOptimizationAssistanceOn = (state: FictivStateType) => {
    return isFlagEnabled(
        getUserIdentity(state),
        PROVISIONED_UI_FLAGS.QLT_OPTIMIZATION_ASSISTANCE,
        'true',
    )
}
export const isOboState = (state: FictivStateType) => {
    const quote = state.quoteQllt?.costAndLeadTimeData
    let isObo = false
    const pathname = window.location.pathname

    if (pathname?.includes('admin/app/user/')) {
        const quoteUserId = quote?.user?.id
        const { id: loggedInUserId, isAdmin } = getUserIdentity(state)

        isObo = isAdmin && loggedInUserId !== quoteUserId
    }
    return isObo
}

export const QLLTHatSelector = createSelector(
    isQLTOptimizationAssistanceOn,
    getNeedByDate,
    (state: FictivStateType) => state.quoteQllt.costAndLeadTimeData?.id,
    isNoTargetDeliveryDateState,
    isTargetDeliveryDateMetState,
    isTargetDeliveryDateNotMetState,
    (state: FictivStateType) => state.quoteQllt,
    (state: FictivStateType) => state,
    isOboState,
    (
        isQLTOptimizationAssistanceFlag,
        needByDate,
        quoteId,
        isTargetDateNotAvailable,
        isTargetDateMet,
        isTargetDateNotMet,
        quoteQllt,
        state,
        isObo,
    ) => {
        const quote = quoteQllt?.costAndLeadTimeData
        const isNeedRfq = quote?.quoteModels?.items?.some(
            item => item.selectedQuoteModelRevision?.state === NEEDS_RFQ,
        )

        return {
            isQLTOptimizationAssistanceFlag,
            needByDate,
            quoteId,
            isTargetDateNotAvailable,
            isTargetDateMet,
            isTargetDateNotMet,
            isSetNeedByDateLoading: quoteQllt?.queries?.setNeedByDate?.loading,
            isObo,
            isNeedRfq,
            selectedShippingOption:
                state?.quoteDetail?.[quoteId]?.selectedShippingOption || null,
            isFreightShipping:
                state?.quoteDetail?.[quoteId]?.isFreightShipping || false,
        }
    },
)

export const QLLTBannerSelector = createSelector(
    isQueryDetailsQueryRefactorOn,
    getNeedByDate,
    getQuoteId,
    getUserIdentity,
    isNoTargetDeliveryDateState,
    isTargetDeliveryDateMetState,
    isTargetDeliveryDateNotMetState,
    isOboState,
    (state: FictivStateType) => state,
    (
        isQueryDetailsQueryRefactorFlag,
        needBydate,
        quoteId,
        identity,
        isTargetDateNotAvailable,
        isTargetDateMet,
        isTargetDateNotMet,
        isObo,
        state,
    ) => {
        let quoteUserId: number | null | undefined

        if (isQueryDetailsQueryRefactorFlag) {
            quoteUserId = isCoreSuccessState(state.queryRefactor)
                ? state.queryRefactor.coreQuoteDetails?.coreQuoteDetailsData
                      ?.user?.id
                : null
        } else {
            quoteUserId = state.dfmLayers?.quote?.user?.id
        }

        const shippingAddress = getShippingAddress({ state })
        const userids = state.userDetails && Object.keys(state.userDetails)

        return {
            quoteUserId,
            needBydate,
            quoteId,
            identity,
            userId: identity.id,
            isTargetDateNotAvailable,
            isTargetDateNotMet,
            isTargetDateMet,
            isObo,
            shippingAddress,
            userids,
        }
    },
)

export const costAndLeadTimeData = (state: FictivStateType) =>
    state.quoteQllt.costAndLeadTimeData

export const costAndLeadTimeDataSelector = createSelector(
    isQLTOptimizationOn,
    costAndLeadTimeData,
    (state: FictivStateType) =>
        state.quoteQllt.queries.getCostAndLeadTime.loading,
    (
        isQLTOptimizationEnalbled,
        costLeadTimeQuote,
        isCostAndLeadTimeLoading,
    ) => {
        return {
            isQLTOptimizationEnalbled,
            costLeadTimeQuote,
            isCostAndLeadTimeLoading,
        }
    },
)
export const LeadTimeSelectionSelector = createSelector(
    (state: FictivStateType) => state.quoteQllt?.costAndLeadTimeData,
    getQuote,
    getNeedByDate,

    (state: FictivStateType) => state.quoteQllt.configureLeadTimeOption,
    (cltQuote, quote, needByDate, configureLeadTimeOption) => {
        const isReorder = cltQuote?.status === REORDERING

        return {
            cltQuote,
            quote,
            needByDate,
            isReorder,
            configureLeadTimeOption,
        }
    },
)
export const partQlltViewSelector = createSelector(
    (state: FictivStateType) => state.quoteQllt.data,
    (state: FictivStateType) => state.quoteQllt.queries.getQlltData.loading,
    (leadTimeData, isQlltDataLoading) => {
        return {
            qmrIds: leadTimeData
                ? Object.keys(leadTimeData).sort(
                      (key1, key2) =>
                          leadTimeData[key1].index - leadTimeData[key2].index,
                  )
                : [],
            isQlltDataLoading:
                isQlltDataLoading &&
                (!leadTimeData || !Object.keys(leadTimeData).length),
        }
    },
)

export const dayRender = (day: number, total?: string) => {
    if (total) {
        if (day == 1) {
            return `${day} production day`
        } else {
            return `${day} production days`
        }
    } else if (day == 1) {
        return `${day} day`
    } else {
        return `${day} days`
    }
}

export const qmrTableSelector = (state: FictivStateType, qmrId: string) => {
    const qlltData = state.quoteQllt.data[qmrId]
    const {
        fileName,
        thumbnailUri,
        version,
        quantity,
        machining,
        postProcesses,
        totalPriceInCents,
        totalTimeInDays,
    } = qlltData
    const processConfig = state.quoteQllt.processConfig || []

    const { removedSecondaryProcess = [] } = processConfig[qmrId] || {}

    const file = {
        fileName,
        thumbnailUri,
        version,
    }

    const quote = state.quoteQllt.costAndLeadTimeData
    const selectedQmr = quote?.quoteModels?.items?.find(
        item => item.selectedQuoteModelRevision.id === qmrId,
    )?.selectedQuoteModelRevision

    const isNeedRfq = selectedQmr?.state === NEEDS_RFQ

    const dataSource: Array<Record<string, unknown>> = [
        {
            key: 'machining',
            name: 'Primary Process',
            days: dayRender(machining.totalTimeInDays),
            cost: getPriceDisplay(machining.totalPriceInCents),
        },
    ]

    if (machining.tightTolerance.totalTimeInDays >= 1 && !isNeedRfq) {
        dataSource.push({
            key: 'tight-tolerances',
            name: 'Tight tolerances',
            days: `Accounts for ${dayRender(
                machining.tightTolerance.totalTimeInDays,
            )}`,
            cost: getMachiningSubItemsPriceDisplay(
                machining.tightTolerance.costInPercentage,
            ),
        })
    }

    if (machining.threads.totalTimeInDays >= 1 && !isNeedRfq) {
        dataSource.push({
            key: 'threads',
            name: 'Threads',
            days: `Accounts for ${dayRender(
                machining.threads.totalTimeInDays,
            )}`,
            cost: getMachiningSubItemsPriceDisplay(
                machining.threads.costInPercentage,
            ),
        })
    }
    const sortedPostProcesses = [
        ...postProcesses
            .filter(process => process.totalTimeInDays > 0)
            .map(process => ({
                key: process.postProcessInfo.postProcess.id,
                name: process.title,
                totalTimeInDays: process.totalTimeInDays,
                totalPriceInCents: process.totalPriceInCents,
                days: dayRender(process.totalTimeInDays),
                cost: getPriceDisplay(process.totalPriceInCents),
                extra: {
                    info: process.postProcessInfo,
                },
            })),
        ...removedSecondaryProcess.map(({ info }) => ({
            key: `removed-${info.postProcess.id}`,
            name: info.postProcess.name,
            totalTimeInDays: info.postProcessLeadTimeInDays || 0,
            totalPriceInCents: (info.unitPriceInCents || 0) * quantity,
            days: dayRender(info.postProcessLeadTimeInDays || 0),
            cost: getPriceDisplay((info.unitPriceInCents || 0) * quantity),
            extra: {
                info: info,
                isRemoved: true,
            },
        })),
    ].sort(
        (process1, process2) =>
            process2.totalTimeInDays - process1.totalTimeInDays ||
            process2.totalPriceInCents - process1.totalPriceInCents,
    )

    dataSource.push(...sortedPostProcesses)
    dataSource.push({
        key: 'total',
        name: 'Total',
        days: dayRender(totalTimeInDays, 'total'),
        cost: getPriceDisplay(totalPriceInCents),
        extra: {
            quantity,
            qmrId,
        },
    })

    const leadTimeData = state.quoteQllt.data
    const batchPostProcessIds = state.quoteQllt.batchPostProcessIds
    const isObo = isOboState(state)
    const holesList = selectedQmr?.holes?.map(hole => hole.id) || []
    const {
        getQlltData,
        updateProcessConfigs,
        removeAllThreads,
        getCostAndLeadTime,
    } = state.quoteQllt.queries

    const isLoading =
        getQlltData.loading ||
        updateProcessConfigs.loading ||
        removeAllThreads.loading ||
        getCostAndLeadTime.loading

    return {
        file,
        dataSource,
        selectedQmr,
        quote,
        isNeedRfq,
        leadTimeData,
        batchPostProcessIds,
        isObo,
        isHavingPlatformThreads: !selectedQmr?.attachmentLink?.id,
        holesList,
        isLoading,
        processConfig,
    }
}

export const getFormattedQmrData = (data: StateData, part: string) => {
    const QMR = { id: '', fileName: '' }

    QMR.id = part
    QMR.fileName = data[part]?.fileName
    return QMR
}

export const dayTotalCellSelector = (state: FictivStateType, qmrId: string) => {
    const quote = state.quoteQllt.costAndLeadTimeData

    const selectedQmr = quote?.quoteModels?.items?.find(
        item => item.selectedQuoteModelRevision.id === qmrId,
    )?.selectedQuoteModelRevision

    return {
        configureLeadTimeOption: state.quoteQllt.configureLeadTimeOption,
        selectedQmr,
        isSpeedCellDisabled:
            Boolean(quote?.status && quote?.status !== DRAFT) ||
            Boolean(quote?.status && quote?.status === REORDERING) ||
            Boolean(
                selectedQmr?.quantityRangeStatus === QUANTITY_RANGE_UNAVAILABLE,
            ),
        quote,
        isQueryRefactorEnabled: isQueryDetailsQueryRefactorOn(state),
    }
}
export const SlackthreadButtonSelector = createSelector(
    (state: FictivStateType) => state?.quoteQllt.slackThreadUrl,
    (state: FictivStateType) =>
        state.quoteQllt.queries.getQlltSlackThreadData.loading,
    (state: FictivStateType) =>
        state.quoteQllt.queries.createSlackThread.loading,
    (slackThreadUrl, isLoading, isThreadCreating) => {
        return {
            slackThreadUrl,
            isLoading,
            isThreadCreating,
        }
    },
)

export const configurationModalWrapperSelector = createSelector(
    isQueryDetailsQueryRefactorOn,
    (state: FictivStateType) => state.dfmLayers?.selectedQuoteModelRevisions,
    (state: FictivStateType) => state.dfmLayers?.quote,
    (state: FictivStateType) => state.graphql?.apolloClient,
    (state: FictivStateType) => state.quoteQllt?.isEditPdfMode,
    (state: FictivStateType) => state.quoteQllt?.showConfigIn3D,
    (
        isQueryDetailsQueryRefactorEnabled,
        selectedQuoteModelRevisions,
        quote,
        apolloClient,
        editPdfMode,
        showConfigIn3D,
    ) => ({
        isQueryDetailsQueryRefactorEnabled,
        selectedQuoteModelRevisions,
        quote,
        apolloClient,
        editPdfMode,
        showConfigIn3D,
    }),
)

export const enableLTOSelector = createSelector(
    getUserIdentity,
    (state: FictivStateType) => state?.quoteQllt?.businessAccountCLTFeature,

    (identity, businessAccountCLTFeature) => {
        const isCLTFeatureEnabled =
            businessAccountCLTFeature?.isEnabled || false

        const isBusinessAdmin = identity?.isBusinessAdmin || false

        const featureId = businessAccountCLTFeature?.id || null

        const cltEnabledAt = businessAccountCLTFeature?.updatedAt || null

        return {
            isCLTFeatureEnabled,
            cltEnabledAt,
            isBusinessAdmin,
            featureId,
        }
    },
)
export const quoteModelByQMRId = (
    quoteModels: CostAndLeadTime_quote_quoteModels_items[] | undefined,
    qmrId: string,
) => {
    if (!quoteModels) {
        return null
    }
    const foundQuoteModel = quoteModels.find(
        quoteModel => quoteModel.selectedQuoteModelRevision.id === qmrId,
    )

    return foundQuoteModel
}

export const getPartLateBy = (state: FictivStateType, qmrId: string) => {
    const needByDate = getNeedByDate(state)
    const qmrLeadTimeDistances = qmrLeadTimeDistancesSelector(state)
    const individualLeadTimeDistanceFromQuote = qmrLeadTimeDistances[qmrId] || 0
    const quoteId = getQuoteId(state)
    const deliveredByDate =
        state.quoteDetail?.[quoteId]?.selectedShippingOption?.deliveredByDate
    const partLateByDays =
        needByDate &&
        deliveredByDate &&
        getOrderLateBy({
            deliveredByDate,
            needByDate,
        }) - individualLeadTimeDistanceFromQuote

    return partLateByDays
}
export const getTimezone = (state: FictivStateType) => {
    const quoteId = getQuoteId(state)
    const selectedShippingAddress = getSelectedShippingAddress(state, quoteId)

    return selectedShippingAddress?.timezone || moment.tz.guess()
}
export const setTimezoneToDate = (
    date: Moment | string | null | undefined,
    timezone: string,
): Moment | null => {
    if (!date) {
        return null
    }
    return moment(date).tz('UTC').tz('UTC', true).tz(timezone, true)
}

export const quantitySelectSelector = (state: FictivStateType, qmrId: any) => {
    const isTargetDateNotMet = isTargetDeliveryDateNotMetState(state)
    const quoteModels: CostAndLeadTime_quote_quoteModels_items[] | undefined =
        state.quoteQllt?.costAndLeadTimeData?.quoteModels?.items
    const recommendedQuantityToReduceLeadTime:
        | RecommendedQuantitiesToHitDeliveryDateType[]
        | [] = state.quoteQllt?.recommendedQuantitiesToHitDeliveryDate

    const nullViewModel = {
        isReady: false,
        desiredDeliveryDate: null,
        quoteId: null,
        currentEstimatedDeliveryDate: null,
        quantityToReduceLeadTime: null,
        shouldShowQuantityToReduceLeadTime: false,
        isTargetDateNotMet,
    }
    const quote = state.quoteQllt.costAndLeadTimeData
    const isFreightShipping =
        state?.quoteDetail?.[quote?.id]?.isFreightShipping || false

    const foundQuoteModel = quoteModelByQMRId(quoteModels, qmrId)

    if (!foundQuoteModel) {
        return nullViewModel
    }

    const quantity =
        foundQuoteModel.selectedQuoteModelRevision.modelConfiguration?.quantity

    const quoteId = getQuoteId(state)
    const timezone = getTimezone(state)
    const needByDate = getNeedByDate(state)

    const needByDateWithTimezone =
        setTimezoneToDate(needByDate, timezone)?.toISOString() || null

    const dateString = needByDateWithTimezone
        ? moment(needByDate).format('YYYY-MM-DD')
        : null
    const needByDateModified = dateString ? moment(dateString) : null

    // Set the time of the needByDate to the end of the business day in the time
    // zone of the shipping address and convert its time zone to UTC.
    needByDateModified?.hour(15).minute(30)
    needByDateModified?.utc()

    const deliveredByDate =
        state.quoteDetail?.[quoteId]?.selectedShippingOption?.deliveredByDate

    if (!quantity || !needByDate || !deliveredByDate) {
        return nullViewModel
    }
    const quantityToReduceLeadTime = recommendedQuantityToReduceLeadTime?.find(
        qmr => qmr?.quoteModelRevisionId === qmrId,
    )?.qty
    const isQuantitySuccess = recommendedQuantityToReduceLeadTime?.find(
        qmr => qmr?.quoteModelRevisionId === qmrId,
    )?.success

    const shouldShowQuantityToReduceLeadTime =
        quantityToReduceLeadTime &&
        quantityToReduceLeadTime !== quantity &&
        isTargetDateNotMet &&
        !isFreightShipping &&
        isQuantitySuccess

    return {
        isReady: true,
        quantity,
        quoteId: quoteId,
        desiredDeliveryDate: needByDateModified?.toISOString() || null,
        currentEstimatedDeliveryDate: deliveredByDate,
        quantityToReduceLeadTime,
        isTargetDateNotMet,
        shouldShowQuantityToReduceLeadTime,
    }
}

export const CostAndLeadTimeSelector = createSelector(
    getUserIdentity,
    getQuote,

    (state: FictivStateType) => state,
    (identity, quote, state) => {
        const shippingAddress = getShippingAddress({ state })

        return shippingAddress
    },
)
export const partNameSelector = (state: FictivStateType, qmrId: string) => {
    const quote = state.quoteQllt.costAndLeadTimeData
    const selectedQmr = quote?.quoteModels?.items?.find(
        item => item.selectedQuoteModelRevision.id === qmrId,
    )?.selectedQuoteModelRevision
    const qlltData = state.quoteQllt.data[qmrId]
    const { fileName, thumbnailUri, version } = qlltData
    const isObo = isOboState(state)
    const { id: loggedInUserId, isAdmin } = getUserIdentity(state)
    const quoteId = quote?.id
    const quoteUserId = quote?.user?.id
    const isQueryRefactorEnabled = isQueryDetailsQueryRefactorOn(state)
    const quoteType = quote?.type
    const dfmFeedback = selectedQmr?.dfmFeedback
    const allDfm = quoteType === PROCESSID_IM ? [] : dfmFeedback

    const warningDfmCount =
        allDfm?.filter(
            (feedback: FeedBackType) =>
                !feedback.accepted && feedback.severity === DFM_WARNING,
        )?.length || 0

    const failureDfmCount =
        allDfm?.filter(
            (feedback: FeedBackType) =>
                !feedback.accepted && feedback.severity === DFM_FAILURE,
        )?.length || 0

    return {
        selectedQmr,
        fileName,
        thumbnailUri,
        version,
        isAdmin,
        isObo,
        loggedInUserId,
        quoteId,
        quoteUserId,
        isQueryRefactorEnabled,
        quoteType,
        dfmFeedback,
        warningDfmCount,
        failureDfmCount,
    }
}
export const additionalOptionalTagsSelector = (
    state: FictivStateType,
    qmrId: string,
) => {
    const { selectedQmr, isObo, quoteId, quoteUserId } = partNameSelector(
        state,
        qmrId,
    )

    const linkedQuoteAttachmentRevision =
        selectedQmr?.attachmentLink?.quoteAttachmentRevision
    const taps = selectedQmr?.taps
    const holes = selectedQmr?.holes || []

    const numConfiguredHoles = taps?.length

    const numConfigurableHoles = holes.filter(h => h.tapOptions?.length)?.length

    const attachment = linkedQuoteAttachmentRevision?.attachment
    const fileName =
        attachment?.__typename === 'Attachment'
            ? attachment.fileName
            : undefined
    const attachmentFileName = attachment?.annotated
        ? attachment.annotated.fileName
        : fileName
    const shouldShowThreadsTag =
        PROCESSES_WITH_THREADED_TAG.some(
            process => process === selectedQmr?.modelConfiguration?.process?.id,
        ) &&
        numConfiguredHoles !== 0 &&
        numConfigurableHoles !== 0

    return {
        isObo,
        quoteId,
        quoteUserId,
        linkedQuoteAttachmentRevision,
        numConfigurableHoles,
        numConfiguredHoles,
        attachmentFileName,
        shouldShowThreadsTag,
    }
}
export const qmrLeadTimeDistancesSelector = createSelector(
    (state: FictivStateType) => state.quoteQllt.data,
    quoteQlltData => {
        if (!quoteQlltData) {
            return {}
        }

        const qmrLeadTimeDistances = Object.keys(quoteQlltData)
            .reduce(
                (
                    accumulator: Array<{ id: string; totalTimeInDays: number }>,
                    id: string,
                ) => {
                    const { totalTimeInDays } = quoteQlltData[id]

                    accumulator.push({ id, totalTimeInDays })
                    return accumulator
                },
                [],
            )
            .sort((a, b) => a.totalTimeInDays - b.totalTimeInDays)
            .reduce(
                (
                    accumulator: { [id: string]: number },
                    shortestLeadTimeQMR,
                    index,
                    qmrTotalDaysAscending,
                ) => {
                    const longestLeadTimeQMR =
                        qmrTotalDaysAscending[qmrTotalDaysAscending.length - 1]
                    const leadTimeDistance =
                        longestLeadTimeQMR.totalTimeInDays -
                        shortestLeadTimeQMR.totalTimeInDays

                    accumulator[shortestLeadTimeQMR.id] = leadTimeDistance

                    return accumulator
                },
                {},
            )

        return qmrLeadTimeDistances
    },
)

export const latenessMessageSelector = (
    state: FictivStateType,
    qmrId: string,
) => {
    const quote = state.quoteQllt.costAndLeadTimeData

    const needByDate = getNeedByDate(state)

    const isTargetDateMet = isTargetDeliveryDateMetState(state)
    const isTargetDateNotMet = isTargetDeliveryDateNotMetState(state)
    const isTargetDateNotAvailable = isNoTargetDeliveryDateState(state)

    const selectedQmr = quote?.quoteModels?.items?.find(
        item => item.selectedQuoteModelRevision.id === qmrId,
    )?.selectedQuoteModelRevision

    const isNeedRfq = selectedQmr?.state === NEEDS_RFQ

    const partLateByDays = getPartLateBy(state, qmrId)

    return {
        isTargetDateMet: isTargetDateMet || (partLateByDays <= 0 && needByDate),
        isTargetDateNotMet: isTargetDateNotMet && partLateByDays > 0,
        isTargetDateNotAvailable,
        isNeedRfq,
        orderLateByDays: partLateByDays,
        selectedShippingOption:
            state?.quoteDetail?.[quote?.id]?.selectedShippingOption || null,
        isFreightShipping:
            state?.quoteDetail?.[quote?.id]?.isFreightShipping || false,
    }
}
export const AddressOptionModalSelector = createSelector(
    isQueryDetailsQueryRefactorOn,
    getUserIdentity,
    isOboState,
    (state: FictivStateType) => state,
    (isQueryDetailsQueryRefactorFlag, identity, isObo, state) => {
        let quoteUserId: number | null | undefined

        if (isQueryDetailsQueryRefactorFlag) {
            quoteUserId = isCoreSuccessState(state.queryRefactor)
                ? state.queryRefactor.coreQuoteDetails?.coreQuoteDetailsData
                      ?.user?.id
                : null
        } else {
            quoteUserId = state.dfmLayers.quote?.user?.id
        }
        return {
            quoteUserId,
            identity,
            isObo,
        }
    },
)

export const targetDateNotMetBannerSelector = createSelector(
    getNeedByDate,
    getQuoteId,
    (state: FictivStateType) => state,
    (needBydate, quoteId, state) => {
        const isFreightShipping =
            state?.quoteDetail?.[quoteId]?.isFreightShipping || false

        return {
            needBydate,
            isFreightShipping,
        }
    },
)

export const useGetQlltSelectedOption = () => {
    return useSelector((state: FictivStateType) => {
        return state.quoteQllt.qlltSelectedOption
    })
}
